import { format, fromUnixTime } from "date-fns/esm";
import badgeUnlockedRaw from "shared/UserGuides/assets/badge-unlocked.svg?raw";
import logoSymbolRaw from "ds/components/SpaceliftLogo/logo-symbol.svg?raw";
import logoTextRaw from "ds/components/SpaceliftLogo/logo-text.svg?raw";
import interRegularUrl from "ds/styles/fonts/Inter/Inter-Regular.woff2?url";
import interMediumUrl from "ds/styles/fonts/Inter/Inter-Medium.woff2?url";

import { Certificate } from "../useUserGuideCertificates";
import certBgRaw from "./certificate-background.svg?raw";
import { createCanvasMeasurer, escapeXml, fitText } from "./textLayout";
import { bytesToBase64, svgToDataUrl } from "./svgEncoding";

const CERTIFICATE_SVG_WIDTH = 800;
const CERTIFICATE_SVG_HEIGHT = 800;

const FONT_FAMILY = "Inter, sans-serif";
const FONT_WEIGHT_REGULAR = 400;
const FONT_WEIGHT_MEDIUM = 500;
// Font weights actually used in the certificate. Only these get embedded into the SVG.
const FONT_WEIGHTS: Array<{ weight: number; url: string }> = [
  { weight: FONT_WEIGHT_REGULAR, url: interRegularUrl },
  { weight: FONT_WEIGHT_MEDIUM, url: interMediumUrl },
];

const COLOR_PRIMARY = "#161618";
const COLOR_SECONDARY = "#6f6f7a";

// Natural viewBox dimensions of the source SVG assets.
const BADGE_NATURAL_SIZE = 72;
const LOGO_NATURAL_HEIGHT = 30;
const LOGO_TEXT_NATURAL_WIDTH = 67;
const LOGO_SYMBOL_NATURAL_WIDTH = 32;
// Mirrors `margin-right: -0.8rem` in SpaceliftLogoWithText so the symbol overlaps the text.
const LOGO_TEXT_SYMBOL_OVERLAP = -4;
const CONTENT_PADDING = 107;
const BADGE_RENDER_SIZE = 115;
const BADGE_TOP = 199;
const LOGO_RENDER_HEIGHT = 38;
const FOOTER_DATE_BASELINE_OFFSET = 236;
const FOOTER_DATE_FONT_SIZE = 21;
const FOOTER_DATE_LABEL_GAP = 7;
const FOOTER_DATE_LABEL_TEXT = "Date of issue:";
const TEXT_MAX_WIDTH = CERTIFICATE_SVG_WIDTH - 2 * CONTENT_PADDING;
const MAX_TEXT_LINES = 2;
const LINE_HEIGHT_EM = 1.1;
// Lower bound (relative to the desired font size) when auto-shrinking text to fit.
const MIN_FONT_SCALE = 0.7;
const SECTION_LABEL_FONT_SIZE = 21;
const COURSE_LABEL_BASELINE = 361;
const COURSE_NAME_BASELINE = 413;
// Course/display name use a smaller font when wrapping is required, so a 2-line value
// doesn't blow past the date row at the bottom of the cert.
const COURSE_NAME_FONT_SIZE = 38;
const COURSE_NAME_WRAPPED_FONT_SIZE = 31;
// When the course name wraps, the first line shifts up by this amount (closer to its label)
// keeping spacing visually proportional to the smaller wrapped font.
const COURSE_NAME_WRAP_COMPRESSION = 9;
const ISSUED_TO_LABEL_BASELINE = 467;
const DISPLAY_NAME_BASELINE = 511;
const DISPLAY_NAME_FONT_SIZE = 29;
const DISPLAY_NAME_WRAPPED_FONT_SIZE = 25;
// Even tighter font used when both the course name AND the display name wrap, so the combined
// 2-line + 2-line stack still leaves a comfortable gap above the date row.
const DISPLAY_NAME_TIGHT_WRAPPED_FONT_SIZE = 22;

// The cert background is embedded as a self-contained base64 <image> — much more reliable
// than inlining its <defs>/filters/gradients as a <g>, which can break when the parent SVG
// is rasterized to canvas.
const stripSvgRoot = (raw: string) =>
  raw
    .replace(/<\?xml[^>]*\?>\s*/g, "")
    .replace(/<svg[^>]*>/, "")
    .replace(/<\/svg>\s*$/, "");

// Fetches the Inter woff2 files and emits a `<style>@font-face{...}</style>` block with the
// fonts inlined as base64. Required because the SVG is rasterized via `<img>` → `<canvas>`,
// which has an opaque origin — it can't fetch the app's fonts itself, so they must travel
// inside the SVG payload.
const buildEmbeddedFontStyles = async (): Promise<string> => {
  const rules = await Promise.all(
    FONT_WEIGHTS.map(async ({ weight, url }) => {
      const buffer = await fetch(url).then((r) => r.arrayBuffer());
      const base64 = bytesToBase64(new Uint8Array(buffer));
      return `@font-face{font-family:"Inter";font-weight:${weight};font-style:normal;src:url(data:font/woff2;base64,${base64}) format("woff2");}`;
    })
  );
  return `<style type="text/css"><![CDATA[${rules.join("")}]]></style>`;
};

const measurerFactory = createCanvasMeasurer(FONT_FAMILY);

type RenderedText = {
  svg: string;
  blockShift: number;
  wrapped: boolean;
};

type RenderCenteredMultilineTextOptions = {
  text: string;
  baseline: number;
  wrapCompression: number;
  fontSize: number;
  wrappedFontSize: number;
  fontWeight: number;
  fill: string;
  breakLongWords?: boolean;
};

const renderCenteredMultilineText = ({
  text,
  baseline,
  wrapCompression,
  fontSize,
  wrappedFontSize,
  fontWeight,
  fill,
  breakLongWords = false,
}: RenderCenteredMultilineTextOptions): RenderedText => {
  // Prefer the full-size single-line layout when it fits — wrapping kicks in only when the
  // text overflows the available width, and uses a smaller font so the extra line doesn't
  // push the layout into the footer.
  const fitsAtFullSize = measurerFactory(fontSize, fontWeight)(text) <= TEXT_MAX_WIDTH;
  if (fitsAtFullSize) {
    return {
      svg: `<text x="${CENTER_X}" y="${baseline}" font-size="${fontSize}" font-weight="${fontWeight}" fill="${fill}">${escapeXml(text)}</text>`,
      blockShift: 0,
      wrapped: false,
    };
  }

  const { lines, fontSize: renderedFontSize } = fitText(
    text,
    wrappedFontSize,
    fontWeight,
    TEXT_MAX_WIDTH,
    MAX_TEXT_LINES,
    measurerFactory,
    MIN_FONT_SCALE,
    breakLongWords
  );

  const lineHeight = renderedFontSize * LINE_HEIGHT_EM;
  // When wrapping, lift the first line up by `wrapCompression` so it sits closer to its
  // label, and subtract the same amount from the downstream shift — yielding a tighter
  // 2-line block while keeping one-liners untouched.
  const firstLineBaseline = baseline - wrapCompression;
  const extraHeight = (lines.length - 1) * lineHeight;
  const lineSpans = lines
    .map(
      (line, i) =>
        `<tspan x="${CENTER_X}" dy="${i === 0 ? 0 : lineHeight}">${escapeXml(line)}</tspan>`
    )
    .join("");

  return {
    svg: `<text x="${CENTER_X}" y="${firstLineBaseline}" font-size="${renderedFontSize}" font-weight="${fontWeight}" fill="${fill}">${lineSpans}</text>`,
    blockShift: extraHeight - wrapCompression,
    wrapped: true,
  };
};

const LOGO_SCALE = LOGO_RENDER_HEIGHT / LOGO_NATURAL_HEIGHT;
const LOGO_TEXT_WIDTH = LOGO_TEXT_NATURAL_WIDTH * LOGO_SCALE;
const LOGO_OVERLAP = LOGO_TEXT_SYMBOL_OVERLAP * LOGO_SCALE;
const LOGO_TOTAL_WIDTH = LOGO_TEXT_WIDTH + LOGO_OVERLAP + LOGO_SYMBOL_NATURAL_WIDTH * LOGO_SCALE;
const LOGO_TEXT_X = CERTIFICATE_SVG_WIDTH - CONTENT_PADDING - LOGO_TOTAL_WIDTH;
const LOGO_SYMBOL_X = LOGO_TEXT_X + LOGO_TEXT_WIDTH + LOGO_OVERLAP;

const BADGE_X = (CERTIFICATE_SVG_WIDTH - BADGE_RENDER_SIZE) / 2;
const BADGE_SCALE = BADGE_RENDER_SIZE / BADGE_NATURAL_SIZE;

// Footer baseline is pinned at the worst-case 2-liner position so the date sits in the
// same place regardless of whether the course/display names wrap. `MAX_FOOTER_SHIFT`
// captures the largest possible vertical shift the lower section can produce (both names
// wrap with the tight font); fixed footer Y so 2-liner permutations
// never collide with the date row, and 1-liners get a date at the same low spot.
const MAX_FOOTER_SHIFT =
  (MAX_TEXT_LINES - 1) * COURSE_NAME_WRAPPED_FONT_SIZE * LINE_HEIGHT_EM -
  COURSE_NAME_WRAP_COMPRESSION +
  (MAX_TEXT_LINES - 1) * DISPLAY_NAME_TIGHT_WRAPPED_FONT_SIZE * LINE_HEIGHT_EM;
const FOOTER_Y = CERTIFICATE_SVG_HEIGHT - FOOTER_DATE_BASELINE_OFFSET + MAX_FOOTER_SHIFT;
const LOGO_TOP = FOOTER_Y - LOGO_RENDER_HEIGHT;
const CENTER_X = CERTIFICATE_SVG_WIDTH / 2;

const CERT_BG_DATA_URL = svgToDataUrl(certBgRaw);
const BADGE_INNER = stripSvgRoot(badgeUnlockedRaw);
const LOGO_SYMBOL_INNER = stripSvgRoot(logoSymbolRaw);
const LOGO_TEXT_INNER = stripSvgRoot(logoTextRaw);

export const buildCertificateSvg = async (
  certificate: Certificate,
  // The browser renders the SVG to a bitmap at this pixel size when it's loaded via <img>.
  // Defaults to the viewBox dimensions; pass a larger value (e.g. 1200) so downstream
  // <canvas> drawImage doesn't have to bitmap-upscale, keeping text/edges crisp at high
  // output resolutions.
  renderSize: number = CERTIFICATE_SVG_WIDTH
): Promise<string> => {
  const fontStyles = await buildEmbeddedFontStyles();
  const dateLabelWidth = measurerFactory(
    FOOTER_DATE_FONT_SIZE,
    FONT_WEIGHT_MEDIUM
  )(FOOTER_DATE_LABEL_TEXT);
  const dateValueX = CONTENT_PADDING + dateLabelWidth + FOOTER_DATE_LABEL_GAP;

  const courseName = renderCenteredMultilineText({
    text: certificate.course,
    baseline: COURSE_NAME_BASELINE,
    wrapCompression: COURSE_NAME_WRAP_COMPRESSION,
    fontSize: COURSE_NAME_FONT_SIZE,
    wrappedFontSize: COURSE_NAME_WRAPPED_FONT_SIZE,
    fontWeight: FONT_WEIGHT_MEDIUM,
    fill: COLOR_PRIMARY,
  });

  // Push the ISSUED TO block (and everything anchored to it) down by the net shift the
  // course name produced when it wrapped, so a 2-line course never collides with the label below.
  const issuedToBaseline = ISSUED_TO_LABEL_BASELINE + courseName.blockShift;
  const displayNameBaseline = DISPLAY_NAME_BASELINE + courseName.blockShift;

  // When the course already wrapped, fall back to a tighter wrapped font for the display name
  // so a 2-line + 2-line stack still leaves a comfortable gap above the date row.
  const displayWrappedFontSize = courseName.wrapped
    ? DISPLAY_NAME_TIGHT_WRAPPED_FONT_SIZE
    : DISPLAY_NAME_WRAPPED_FONT_SIZE;
  const displayName = renderCenteredMultilineText({
    text: certificate.displayName,
    baseline: displayNameBaseline,
    wrapCompression: 0,
    fontSize: DISPLAY_NAME_FONT_SIZE,
    wrappedFontSize: displayWrappedFontSize,
    fontWeight: FONT_WEIGHT_MEDIUM,
    fill: COLOR_PRIMARY,
    breakLongWords: true,
  });

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${CERTIFICATE_SVG_WIDTH} ${CERTIFICATE_SVG_HEIGHT}" width="${renderSize}" height="${renderSize}">
  ${fontStyles}
  <image href="${CERT_BG_DATA_URL}" x="0" y="0" width="${CERTIFICATE_SVG_WIDTH}" height="${CERTIFICATE_SVG_HEIGHT}" preserveAspectRatio="none" />

  <g transform="translate(${BADGE_X} ${BADGE_TOP}) scale(${BADGE_SCALE})">${BADGE_INNER}</g>

  <g font-family="${FONT_FAMILY}" text-anchor="middle" font-size="${SECTION_LABEL_FONT_SIZE}" font-weight="${FONT_WEIGHT_MEDIUM}" fill="${COLOR_SECONDARY}">
    <text x="${CENTER_X}" y="${COURSE_LABEL_BASELINE}">COURSE CERTIFICATE</text>
    ${courseName.svg}
    <text x="${CENTER_X}" y="${issuedToBaseline}">ISSUED TO</text>
    ${displayName.svg}
  </g>

  <g font-family="${FONT_FAMILY}" fill="${COLOR_PRIMARY}">
    <text x="${CONTENT_PADDING}" y="${FOOTER_Y}" font-size="${FOOTER_DATE_FONT_SIZE}" font-weight="${FONT_WEIGHT_MEDIUM}">${FOOTER_DATE_LABEL_TEXT}</text>
    <text x="${dateValueX}" y="${FOOTER_Y}" font-size="${FOOTER_DATE_FONT_SIZE}" font-weight="${FONT_WEIGHT_REGULAR}">${format(fromUnixTime(certificate.issuedAt), "MM/dd/yyyy")}</text>
  </g>

  <g color="${COLOR_PRIMARY}">
    <g transform="translate(${LOGO_TEXT_X} ${LOGO_TOP}) scale(${LOGO_SCALE})">${LOGO_TEXT_INNER}</g>
    <g transform="translate(${LOGO_SYMBOL_X} ${LOGO_TOP}) scale(${LOGO_SCALE})">${LOGO_SYMBOL_INNER}</g>
  </g>
</svg>`;
};
